import { Component, EventEmitter, Input, Output, OnInit, DoCheck, OnChanges, SimpleChanges } from '@angular/core';
import { NavigationProvider } from '../../services/navigation.provider';
import { StatsService } from '../../services/stats.service';
import { StorageService } from '../../services/storage.service';
import { CourseSubscriptionSessionProvider } from '@stuplay';
import { take } from 'rxjs';
import { Router } from '@angular/router';

@Component({
    selector: 'msc-next-prev-btn',
    templateUrl: './next-prev-btn.component.html'
})
export class MscNextPrevComponent implements OnInit, DoCheck, OnChanges {
    @Input() validate: boolean = false;
    @Input() translator: string = '';
    @Input() doneAt: any;
    @Input() lastQuestion: boolean = false;
    @Input() navigate: boolean = true;
    @Input() type: string = 'default';
    @Input() loading: boolean = false;
    @Input() navigateEmitter: EventEmitter<boolean> = new EventEmitter();
    @Output() isFinished: EventEmitter<boolean> = new EventEmitter();
    currentSectionIsCompleted: boolean = false;
    course: any;
    nextAvailable: boolean = true;
    display: boolean = true;
    // NUEVO: bandera que se activa cuando el alumno hace click en "Completar y continuar".
    // Sirve para alternar el botón derecho de la barra unificada a "Siguiente".
    activityCompleted: boolean = false;

    constructor(
        private navigationProvider: NavigationProvider, 
        private statsService: StatsService, 
        private storageService: StorageService, 
        private courseSubscriptionSessionProvider: CourseSubscriptionSessionProvider,
        private router: Router
    ) { }

    ngOnInit() {
        this.course = this.storageService.get('course');
        this.display = !this.course.isTrajectContent || (this.course.isTrajectContent && this.course.templateId) || this.course.sections[0].sectionContents[0].context === 'exercise';

        this.currentSectionIsCompleted = this.navigationProvider.currentCourseSubscriptionSession.getSectionProgress(this.navigationProvider.currentSection) === 1;

        this.navigateEmitter.subscribe(() => {
            this.navigation();
        });
    }

    goNextOrPrev(direction: string) {
        if (!document?.getElementById('view')?.scroll) { return; }
        document.getElementById('view').scroll(0, 0);
        this.navigationProvider.goRoute(direction);
    }

    // NUEVO: cuando cambia el contenido (cambia doneAt o type al navegar a otra lección),
    // se reinicia activityCompleted para que vuelva a aparecer "Completar y continuar"
    // en el contenido nuevo aún no visto.
    ngOnChanges(changes: SimpleChanges) {
        if (changes['doneAt'] || changes['type']) {
            this.activityCompleted = false;
        }
    }

    ngDoCheck() {
        this.nextAvailable = this.navigationProvider.isNextSectionAvailable();
    }

    // NUEVO: deshabilita "Enviar y continuar" mientras la actividad de tipo project/selfassessment
    // no tenga un archivo cargado. `validate` refleja mediasId.length > 0 (media subida con 200)
    // y solo pasa a true cuando la subida termina correctamente. Para el resto de actividades
    // (que pasan validate=true) el botón nunca se bloquea por esta condición.
    isSubmitDisabled(): boolean {
        if (this.loading) { return true; }
        if (this.type === 'project' || this.type === 'selfassessment') {
            return !this.validate;
        }
        return false;
    }

    activityFinished() {
        // Bloqueo defensivo: aunque el botón esté deshabilitado por CSS/atributo,
        // evitamos completar la actividad si no hay archivo cargado.
        if (this.isSubmitDisabled()) { return; }
        if (!this.loading) {
            // NUEVO: marca la actividad como completada para que la barra unificada
            // reemplace "Completar y continuar" por "Siguiente".
            this.activityCompleted = true;
            this.isFinished.emit(true);

            if (this.navigate) {
                this.process();
            }
        }
    }

    // NUEVO: indica si en el slot derecho de la barra unificada debe mostrarse
    // el botón "Completar y continuar". Reproduce las mismas condiciones del *ngIf original
    // del botón #btn-course y además exige que aún no se haya completado.
    showCompleteButton(): boolean {
        if (this.isSelfReportingActivity()) { return false; }
        // Mostrar "Completar y continuar" en cualquier contenido aún no visto (doneAt vacío)
        // y que no se haya completado en esta sesión. Al hacer click, activityCompleted
        // pasa a true y en el mismo slot se renderiza el botón "Siguiente".
        return !this.doneAt && !this.activityCompleted;
    }

    // El SCORM Cloud informa su propio progreso a la API: el alumno no lo marca a mano
    // y puede seguir aunque no lo haya terminado (salvo bloqueo de módulos ordenados).
    // En los cursos sin navegación interna (display=false) se conserva el botón de cierre.
    private isSelfReportingActivity(): boolean {
        return this.type === 'scormcloud' && this.display;
    }

    // NUEVO: navega a la lección anterior usando el provider existente (misma lógica de scroll que goNextOrPrev).
    goPrev(): void {
        if (!document?.getElementById('view')?.scroll) {
            this.navigationProvider.goRoute('prev');
            return;
        }
        document.getElementById('view').scroll(0, 0);
        this.navigationProvider.goRoute('prev');
    }

    // NUEVO: navega a la siguiente lección. Respeta isNextDisabled() para no avanzar si el botón está deshabilitado.
    goNextNav(): void {
        if (this.isNextDisabled()) { return; }
        if (!document?.getElementById('view')?.scroll) {
            this.navigationProvider.goRoute('next');
            return;
        }
        document.getElementById('view').scroll(0, 0);
        this.navigationProvider.goRoute('next');
    }

    // NUEVO: deshabilita "Siguiente" cuando la actividad requiere completarse y no se ha completado,
    // o cuando es el último contenido de la última sección disponible.
    isNextDisabled(): boolean {
        // Si el contenido aún no se ha visto y no se hizo click en "Completar y continuar",
        // se considera que el botón "Siguiente" no debería estar activo (aunque en ese caso
        // tampoco se renderiza porque showCompleteButton() es true).
        if (!this.doneAt && !this.activityCompleted && !this.isSelfReportingActivity()) { return true; }

        // Dentro de una evaluación con varias preguntas, mientras no sea la última pregunta
        // el botón "Siguiente" debe habilitarse para avanzar a la siguiente pregunta,
        // sin importar si el módulo siguiente está bloqueado (eso solo aplica al salir
        // de la evaluación completa).
        if (this.type === 'question' && !this.lastQuestion) { return false; }

        return this.navigationProvider.isLastSectionContent() && !this.navigationProvider.isNextSectionAvailable();
    }

    // NUEVO: posición (1-based) de la lección actual dentro del módulo/sección actual.
    getCurrentLessonIndex(): number {
        const contents = this.navigationProvider.currentSection?.sectionContents || [];
        const idx = contents.findIndex((sc: any) => sc.id === this.navigationProvider.currentSectionContent?.id);
        return idx >= 0 ? idx + 1 : 1;
    }

    // NUEVO: total de lecciones (sectionContents) del módulo/sección actual.
    getTotalLessons(): number {
        return (this.navigationProvider.currentSection?.sectionContents || []).length;
    }

    process(): void {
        if (this.display) {
            const timeOut = setTimeout(() => {
                this.navigation();
                clearTimeout(timeOut);
            }, 1500);
        } else {
            const timeOut = setTimeout(() => {
                window.location.href = this.storageService.getFlash('referrer');
                clearTimeout(timeOut);
            }, 3000);
        }
    }

    navigation(): void {
        this.statsService.refresh(true);
        if (this.navigationProvider.isNextSectionAvailable()) {
            this.goNextOrPrev('next');
        }
    }

    public refreshData(): void {
        const currentRoute = this.router.url;
        if (!currentRoute.includes('question')) {
            this.courseSubscriptionSessionProvider.getOrCreateCourseSubscriptionSession(this.course.id).pipe(take(1)).subscribe();
        }
    }
}
